import Broadcast from 'commons/js/Broadcast';
import unionBy from 'lodash/unionBy';
import { getPrintPreference } from 'commons/js/utils/print';

export default {
  async created() {
    await this.loadOrdersInitialState();

    this.addGenerationListeners();
  },

  data() {
    return {
      allOrders: [],
    }
  },

  methods: {
    async getOrdersByPrintKey() {
      try {
        const printQuality = getPrintPreference();

        const {data} = await axios.get(`/api/v2/me/shipment/print/orders/${this.printerKey}`, {
          params: {
            printQuality,
          },
        });

        return data || [];
      } catch (error) {
        console.error(error)
      }
    },

    addGenerationListeners() {
      const channel= Broadcast.user(MEConfig.get('user.id'));
      channel
        .listen('.tag.image.generation.done', (e) => this.handleOneOrderGeneratedDone(e))
        .listen('.tag.image.generation.failed', (e) => this.handleOneOrderGeneratedFailed(e))
        .listen('.tag.order.generated', (e) => this.handleOneOrderGenerated(e))
        .listen('.tag.order.failed', (e) => this.handleOneOrderFailed(e))
        .listen('.tag.order.volume', (e) => this.handleOneVolumeReceived(e))
        .listen('.tag.done', (e) => this.handleAllOrdersGenerated(e))
    },

    handleOneOrderGeneratedDone(eventData) {
      const {
        tagImageLinks,
        orderId,
      } = eventData;

      const orderIndex = this.allOrders.findIndex(order => order.id === orderId);
      const order = this.allOrders[orderIndex];

      if (!order) return;

      const volumesWithImage = order.volumes.map((item, index) => ({
        ...item,
        tagImageLink: tagImageLinks[index]
      }));

      const currentOrder = {
        ...order,
        volumes: volumesWithImage
      };

      this.allOrders.splice(orderIndex, 1, currentOrder);
    },

    handleOneOrderGeneratedFailed(eventData) {
      const {
        orderId
      } = eventData;

      const orderIndex = this.allOrders.findIndex(order => order.id === orderId);
      const order = this.allOrders[orderIndex];

      const currentOrder = {
        ...order,
        t_printer: order.t_printer
          ? { ...order.t_printer, failed_at: true }
          : { failed_at: true }
      };

      this.allOrders.splice(orderIndex, 1, currentOrder);
    },

    handleOneOrderGenerated(eventData) {
      const {
        generate_key,
        order_id,
        generated_at,
        data: {
          correios,
          tracking,
          additional_info,
          authorization_code,
          self_tracking,
          volumes,
        }
      } = eventData;

      const currentOrder = {
        generate_key,
        id: order_id,
        generated_at,
        additional_info,
        tracking,
        authorization_code,
        self_tracking,
        volumes
      };

      if (correios) {
        const [plp] = Object.keys(correios.plps);
        currentOrder.contract = correios.plps[plp].contract;
        currentOrder.correios = correios;
      }

      if (this.generateKey !== generate_key) {
        return;
      }

      this.mergeToArray(currentOrder);
    },

    handleOneOrderFailed(eventData) {
      const {generate_key, order_id, data: {message, failed_at, attempts}} = eventData;

      const currentOrder = {
        generate_key,
        id: order_id,
        generated_key: {
          data: message,
          failed_at,
          attempts,
        }
      };

      if (this.generateKey !== generate_key) {
        return;
      }

      this.mergeToArray(currentOrder);
    },

    handleOneVolumeReceived(eventData) {
      const {order_id, generate_key, volume} = eventData;
      if (this.generateKey !== generate_key) {
        return;
      }

      if (this.allOrders.length) {
        this.completeOrderWithVolume(order_id, volume);
      }
    },

    handleAllOrdersGenerated(eventData) {
      const {generate_key} = eventData;

      if (this.generateKey && (this.generateKey !== generate_key)) {
        return;
      }

      const hasAnyOrderWithoutData = this.allOrders.some(order => {
        return !order.generated_at && !this.isFailedOrder(order);
      });

      hasAnyOrderWithoutData && this.retrieveLostOrders();
    },

    mergeToArray(itemToBeMerged) {
      const itemIndex = this.allOrders.findIndex(item => item.id === itemToBeMerged.id);

      const additionalInfo = this.allOrders[itemIndex].additional_info || {};

      itemToBeMerged.additional_info = {
        ...itemToBeMerged.additional_info,
        ...additionalInfo,
      };

      const tempObj = {
        ...this.allOrders[itemIndex],
        ...itemToBeMerged,
      };

      this.allOrders.splice(itemIndex, 1, tempObj);
    },

    completeOrderWithVolume(order_id, newVolume) {
      const order = this.allOrders.find(order => order.id === order_id);
      order.additional_info = order.additional_info || {};
      order.additional_info.volume = order.additional_info.volume || [];

      const volumeIndex = order.additional_info.volume.findIndex(volume => volume.seqVolume === newVolume.seqVolume);
      (volumeIndex == -1) && order.additional_info.volume.push(newVolume);
    },

    isFailedOrder(order) {
      return order.generated_key && order.generated_key.failed_at;
    },

    async retrieveLostOrders() {
      const retrievedOrders = await this.getOrdersByPrintKey();
      if (!retrievedOrders.length) return;

      const retrievedGeneratedOrders = retrievedOrders.filter(
        order => order.generated_at && !this.isFailedOrder(order)
      );

      this.allOrders = unionBy(retrievedGeneratedOrders, this.allOrders, "id");
    },
  },
}
